import React from "react";
import { Text, plainText } from "../../atoms/text/text";
import { ChannelLogo } from "./channel-logo";
import { TooltipTrigger } from "./indicator";
import styles from "./channel-identity.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The left cell of a grid row: the round logo, the channel name and the channel number, stacked and
 * centred (GR1).
 */
export interface ChannelIdentityProps {
  /** Rendered as PLAIN TEXT, always — see the note on `plainText` below. */
  name: string;
  number?: string;
  logoUrl?: string;
  /**
   * GR5 — an RSN's tier, as a superscript after the name. Drawn only above 1, because tier 1
   * publishes no superscript and an RSN with no tier publishes none at all.
   */
  tier?: number;
  /**
   * GR4 — the Local / RSN / National badge, drawn under the logo and above the name
   * (`16174:27960`). A node rather than a `kind`, so the row owns which badge a channel wears
   * (`localityKindFor`) and this leaf owns only where it goes.
   */
  badge?: React.ReactNode;
  /** AX6 — "tabbing to a channel logo … shows its tooltip, and Esc hides it". */
  showLogoTooltip?: boolean;
  /** Layout only. */
  className?: string;
}

function ChannelIdentityImpl({
  name,
  number,
  logoUrl,
  tier,
  badge,
  showLogoTooltip = false,
  className
}: ChannelIdentityProps) {
  /* GR9 — the live feed ships markup inside a label ("Telemundo East<br/>"), and the criterion is
     that it never shows as formatting or as visible tags. */
  const label = plainText(name);
  const showTier = typeof tier === "number" && tier > 1;
  /* AX6 — a trigger with no name is a focus stop that announces nothing, the
     same guard `logo-grid.tsx` puts on its own tile. */
  const logoTooltip = showLogoTooltip && label !== "";
  /* The trigger wraps the tile; it is not inside it. `logo-grid.tsx` already wraps `ChannelLogo` in
     its own `TooltipTrigger`, so a trigger built into the tile would nest two of them there. */
  /* R1-05.4 — the signature grid's channel disc, stated in full because `ChannelLogo` defaults to
     the one tile no frame draws: 80 desktop / 64 mobile, with the brand art inset to 56 / 44 inside
     it, on the light `--slate-50` disc `14582:26873` paints. */
  const logo = (
    <ChannelLogo
      className={logoTooltip ? styles.logoTile : undefined}
      logoUrl={logoUrl}
      shape="circle"
      size="signature"
      surface="signature"
    />
  );

  return (
    <span className={[styles.identity, className].filter(Boolean).join(" ")}>
      {logoTooltip ?
        <TooltipTrigger content={label} label={label}>
          {logo}
        </TooltipTrigger> :
        logo}
      {/* Badge under the logo, above the name (RSN Row `16174:27960`). */}
      {badge}
      <Text as="span" className={styles.name} variant="body-2">
        {label}
        {showTier && (
          /* Inside the name so it joins the accessible name rather than
             being read as a loose number after it. */
          <sup className={styles.tier}>{tier}</sup>
        )}
      </Text>
      {number && (
        <Text as="span" className={styles.number} variant="body-2">
          {plainText(number)}
        </Text>
      )}
    </span>
  );
}
export const ChannelIdentity = withSafeRender(ChannelIdentityImpl, "ChannelIdentity");

export default ChannelIdentity;
